import React, { useState, useEffect, useRef } from 'react';
import { clone } from 'ramda';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Checkbox, PopupMenu } from 'quantux-ui';
import CaseDoc from '../case-doc';
import { blue, gray, yellow } from '../../theme';
import { FLAG_VALUE } from '../../utils';

const IS_NONE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none"><path fill="#96C8FF" fillRule="evenodd" d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z" clipRule="evenodd" /></svg>
);

const CRITIAL_ICON = (
  <svg
    width="16"
    height="13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 12.364 8 0 0 12.364h16Zm-7.273-2.182H7.273V8.728h1.454v1.454ZM7.273 8h1.454V5.091H7.273v2.91Z"
      fill="#FF0"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);

const IntraOp = (props) => {
  const [showAnesTypeMenu, setShowAnesTypeMenu] = useState(false);
  const [showAnesModMenu, setShowAnesModMenu] = useState(false);
  const [anesType, setAnesType] = useState([]);
  const [anesMod, setAnesMod] = useState([]);
  const [anesModFlag, setAnesModFlag] = useState(FLAG_VALUE.critical);

  // Anesthesia Type(s)
  useEffect(() => {
    if (props.anesthesiaTypes.length !== anesType.length) {
      setAnesType(props.anesthesiaTypes);
    }
  }, [props.anesthesiaTypes]);

  // Anesthesia Modifier(s)
  useEffect(() => {
    if (props.anesthesiaModifiers.length !== anesMod.length) {
      setAnesMod(props.anesthesiaModifiers);
    }
  }, [props.anesthesiaModifiers]);

  // AnesMode Flag
  useEffect(() => {
    if (anesMod.length === 1 && anesMod[0] === 'None') {
      setAnesModFlag(FLAG_VALUE.none);
      return;
    }

    if (anesMod.length === 0) {
      setAnesModFlag(FLAG_VALUE.critical);
    } else {
      setAnesModFlag(FLAG_VALUE.success);
    }
  }, [anesMod]);

  const handleOnClickAnesType = (val) => {
    let updatedAnesType = clone(anesType);
    if (anesType.indexOf(val) < 0) {
      updatedAnesType = updatedAnesType.concat(val);
    } else {
      updatedAnesType = updatedAnesType.filter(el => el !== val);
    }
    setAnesType(updatedAnesType);
    props.onChangeAnesthesiaTypes(updatedAnesType);
  };

  const handleOnClickAnesMod = (val) => {
    let updatedAnesMod = clone(anesMod);
    if (anesMod.indexOf(val) < 0) {
      if (val === 'None') {
        updatedAnesMod = ['None'];
      } else {
        updatedAnesMod = anesMod.concat(val);
      }
    } else {
      updatedAnesMod = anesMod.filter(el => el !== val);
    }
    setAnesMod(updatedAnesMod);
    props.onChangeAnesthesiaModifiers(updatedAnesMod);
  };

  const styles = StyleSheet.create({
    intraOpContainer: {
      backgroundColor: '#002248',
      width: '100%',
      height: 'calc(100% - 42px)'
    },
    dataContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px',
      height: '40px',
      borderBottom: `1px solid ${blue[800]}`
    },
    popupMenuContainer: {
      width: '155px',
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto'
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      background: '#103860',
      // borderRadius: '4px',
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginBottom: '2px',
      height: '40px',
      width: '100%',
      paddingLeft: '8px',
      ':hover:not(.selected)': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    menuItemAvatarImg: {
      height: '40px',
      maxHeight: '40px',
      borderRadius: '4px'
    },
    selectedItem: {
      background: gray[800]
    },
    dataItem: {
      position: 'relative',
      background: blue[700],
      fontWeight: 'bold',
      fontSize: '14px',
      height: '24px',
      paddingRight: '16px',
      paddingLeft: '8px',
      borderRadius: '4px'
    }
  });

  const anesTypeRef = useRef();
  const anesModRef = useRef();

  return (
    <div className={css(styles.intraOpContainer)}>
      {/* Anesthesia Data */}
      <div className={css(styles.dataContainer)}>
        {/* Anes Type */}
        <div
          ref={anesTypeRef}
          style={{ position: 'relative' }}
        >
          <div
            className={`fx fx__hv-center ml-2 ${css(styles.dataItem)}`}
            onClick={() => setShowAnesTypeMenu(true)}
          >
            {anesType.length === 0 ? CRITIAL_ICON : SUCCESS_ICON}
            <span
              className='ml-2'
              style={{
                color: anesType.length > 0 ? gray[300] : yellow[500]
              }}
            >
              AnesType
              {anesType.length > 0 &&
                <span className='ml-2' style={{ color: blue[400] }}>{anesType.join(' | ')}</span>
              }
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="8"
              fill="none"
              style={{
                position: 'absolute',
                bottom: '2px',
                right: '02px'
              }}
            >
              <path fill="#96C8FF" d="M7.146.854A.5.5 0 0 1 8 1.207V5a3 3 0 0 1-3 3H1.207a.5.5 0 0 1-.353-.854L7.146.854Z" />
            </svg>
          </div>
          {showAnesTypeMenu &&
            <PopupMenu
              top={30}
              left={-1}
              zIndex='1550'
              position="absolute"
              visible={showAnesTypeMenu}
              parent={anesTypeRef}
              onOutsideClick={() => { setShowAnesTypeMenu(false); }}
            >
              <div
                className={css(styles.popupMenuContainer)}
                style={{
                  maxHeight: '340px'
                }}
              >
                <div
                  className={`${anesType.indexOf('GA-ETT') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('GA-ETT') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='GA-ETT'
                    isChecked={anesType.indexOf('GA-ETT') > -1}
                    onChange={() => handleOnClickAnesType('GA-ETT')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('GA-LMA') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('GA-LMA') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='GA-LMA'
                    isChecked={anesType.indexOf('GA-LMA') > -1}
                    onChange={() => handleOnClickAnesType('GA-LMA')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('MAC') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('MAC') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='MAC'
                    isChecked={anesType.indexOf('MAC') > -1}
                    onChange={() => handleOnClickAnesType('MAC')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('Epidural') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('Epidural') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Epidural'
                    isChecked={anesType.indexOf('Epidural') > -1}
                    onChange={() => handleOnClickAnesType('Epidural')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('SAB') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('SAB') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='SAB'
                    isChecked={anesType.indexOf('SAB') > -1}
                    onChange={() => handleOnClickAnesType('SAB')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('Regional') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('Regional') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Regional'
                    isChecked={anesType.indexOf('Regional') > -1}
                    onChange={() => handleOnClickAnesType('Regional')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('Local') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('Local') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Local'
                    isChecked={anesType.indexOf('Local') > -1}
                    onChange={() => handleOnClickAnesType('Local')}
                  />
                </div>
                <div
                  className={`${anesType.indexOf('Other') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesType.indexOf('Other') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Other'
                    isChecked={anesType.indexOf('Other') > -1}
                    onChange={() => handleOnClickAnesType('Other')}
                  />
                </div>
              </div>
            </PopupMenu>
          }
        </div>

        {/* Anes Modifiers */}
        <div
          ref={anesModRef}
          style={{ position: 'relative' }}
        >
          <div
            className={`fx fx__hv-center ml-2 ${css(styles.dataItem)}`}
            onClick={() => setShowAnesModMenu(true)}
          >
            {/* Anes Mode Flag */}
            {anesModFlag === FLAG_VALUE.none && IS_NONE_ICON}
            {anesModFlag === FLAG_VALUE.critical && CRITIAL_ICON}
            {anesModFlag === FLAG_VALUE.success && SUCCESS_ICON}
            <span
              className='ml-2'
              style={{
                color: anesMod.length > 0 ? gray[300] : yellow[500]
              }}
            >
              Modifiers
              {anesMod.length > 0 &&
                <span className='ml-2' style={{ color: blue[400] }}>{anesMod.join(' | ')}</span>
              }
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="8"
              fill="none"
              style={{
                position: 'absolute',
                bottom: '2px',
                right: '02px'
              }}
            >
              <path fill="#96C8FF" d="M7.146.854A.5.5 0 0 1 8 1.207V5a3 3 0 0 1-3 3H1.207a.5.5 0 0 1-.353-.854L7.146.854Z" />
            </svg>
          </div>
          {showAnesModMenu &&
            <PopupMenu
              top={30}
              left={-1}
              zIndex='1550'
              position="absolute"
              visible={showAnesModMenu}
              parent={anesModRef}
              onOutsideClick={() => { setShowAnesModMenu(false); }}
            >
              <div
                className={css(styles.popupMenuContainer)}
                style={{
                  maxHeight: '340px',
                  width: '200px'
                }}
              >
                {/* None */}
                <div
                  className={`${anesMod.indexOf('None') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesMod.indexOf('None') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='None'
                    isChecked={anesMod.indexOf('None') > -1}
                    onChange={() => handleOnClickAnesMod('None')}
                  />
                </div>
                {/* Field Avoidance */}
                <div
                  className={`${anesMod.indexOf('Field Avoidance') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesMod.indexOf('Field Avoidance') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Field Avoidance'
                    isDisabled={anesMod.indexOf('None') > -1}
                    isChecked={anesMod.indexOf('Field Avoidance') > -1}
                    onChange={() => {
                      if (anesMod.indexOf('None') < 0) {
                        handleOnClickAnesMod('Field Avoidance');
                      }
                    }}
                  />
                </div>
                {/* Prone */}
                <div
                  className={`${anesMod.indexOf('Prone') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesMod.indexOf('Prone') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Prone'
                    isDisabled={anesMod.indexOf('None') > -1}
                    isChecked={anesMod.indexOf('Prone') > -1}
                    onChange={() => {
                      if (anesMod.indexOf('None') < 0) {
                        handleOnClickAnesMod('Prone');
                      }
                    }}
                  />
                </div>
                {/* Controlled Hypotension */}
                <div
                  className={`${anesMod.indexOf('Controlled Hypotension') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesMod.indexOf('Controlled Hypotension') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Controlled Hypotension'
                    isDisabled={anesMod.indexOf('None') > -1}
                    isChecked={anesMod.indexOf('Controlled Hypotension') > -1}
                    onChange={() => {
                      if (anesMod.indexOf('None') < 0) {
                        handleOnClickAnesMod('Controlled Hypotension');
                      }
                    }}
                  />
                </div>
                {/* Controlled Hypothermia */}
                <div
                  className={`${anesMod.indexOf('Controlled Hypothermia') > -1 ? 'selected' : ''} ${css(styles.menuItem, anesMod.indexOf('Controlled Hypothermia') > -1 && styles.selectedItem)}`}
                >
                  <Checkbox
                    text='Controlled Hypothermia'
                    isDisabled={anesMod.indexOf('None') > -1}
                    isChecked={anesMod.indexOf('Controlled Hypothermia') > -1}
                    onChange={() => {
                      if (anesMod.indexOf('None') < 0) {
                        handleOnClickAnesMod('Controlled Hypothermia');
                      }
                    }}
                  />
                </div>
              </div>
            </PopupMenu>
          }
        </div>
      </div>
      {/* Case Doc */}
      <CaseDoc
        showFlag={props.showFlag}
        formType={props.formType}
        flagType={props.flagType}
        scheduleId={props.scheduleId}
        caseDocs={props.caseDocs}
        isNone={props.isNone}
        onDeleteDocument={props.onDeleteDocument}
        onDeletePage={props.onDeletePage}
        onChangeIsNone={props.onChangeIsNone}
        onCaseDocUploaded={props.onCaseDocUploaded}
        onEdit={props.onEdit}
        onScanDocument={props.onScanDocument}
        onShowCropper={props.onShowCropper}
        onShowCamera={props.onShowCamera}
        onShowDocWriter={props.onShowDocWriter}
      />
    </div>
  );
};

export default IntraOp;
